import { AnimatePresence } from 'motion/react';
import React, { CSSProperties, forwardRef, KeyboardEventHandler } from 'react';
import FocusLock from 'react-focus-lock';
import { ReactFocusLockProps } from 'react-focus-lock';
import { slideVertical } from '../../styles/animation';
import TopicsList, { TopicsListProps } from './TopicsList';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { useTopicsContext } from '../../context/TopicsContext';
import { handleEscapeKey } from '../../lib/util/eventHandlers';
import { LinkObject } from '../../types/topics';
import MotionDiv from '@osmosis/nextjs-components/MotionDiv';

type Props = {
  isVisible: boolean;
  hide?: () => void;
  onSelectTopic?: (topic: LinkObject) => void;
  motionContainerStyle?: CSSProperties;
  topicsListProps?: Pick<TopicsListProps, 'containerSx'>;
  dataTestid?: string;
  topicsListDropdownRef?: React.RefObject<HTMLDivElement>;
  isSearchResultsOpen?: boolean;
  focusLockProps?: ReactFocusLockProps;
};

/** Dropdown that shows animates the topics list. */
const TopicsListDropdown = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    isVisible,
    hide,
    motionContainerStyle = {},
    topicsListProps,
    dataTestid = '',
    onSelectTopic,
    focusLockProps = {
      returnFocus: true,
      autoFocus: true,
    },
    isSearchResultsOpen,
  } = props;

  const { topics } = useTopicsContext();

  let containerWidth = '100%';
  if (typeof window !== 'undefined' && motionContainerStyle.width) {
    containerWidth = motionContainerStyle.width as string;
  }

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    handleEscapeKey(e, () => hide?.());
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <MotionDiv
          data-testid="dashboard-topics-dropdown"
          aria-modal="true"
          role="dialog"
          onKeyDown={handleKeyDown}
          {...slideVertical()}
          style={{
            ...motionContainerStyle,
            position: 'absolute',
            top:
              motionContainerStyle.top ??
              (isSearchResultsOpen ? '100%' : '60%'),
            width: containerWidth,
            maxWidth: 'content',
            display: 'flex',
            justifyContent: 'center',
            zIndex: ZIndex.MODAL,
            left: 0,
            right: 0,
            marginInline: 'auto',
          }}
        >
          <Box
            className="m-auto max-w-[95%]"
            data-testid={dataTestid}
            onClick={(e) => e.stopPropagation()}
            ref={ref}
          >
            <FocusLock {...focusLockProps}>
              <TopicsList
                topics={topics}
                onSelectTopic={onSelectTopic}
                dataTestId="header_topic_dropdown"
                isVideoTopics={true}
                onClose={hide}
                {...topicsListProps}
                containerSx={{
                  maxWidth: '100%',
                  width: 'max-content',
                  margin: '0 auto',
                  maxHeight: 'calc(100vh - 285px)',
                  ...(topicsListProps?.containerSx || {}),
                }}
                containerClassName="mt-2 shadow-lg"
              />
            </FocusLock>
          </Box>
        </MotionDiv>
      )}
    </AnimatePresence>
  );
});

TopicsListDropdown.displayName = 'TopicsListDropdown';

export default TopicsListDropdown;
